import { useEffect, useState, type ReactNode } from "react";
import { I18nextProvider } from "react-i18next";
import { dashboardEngine } from "./engine";
import { createLanguageController } from "./languageController";
import { DashboardLanguageContext } from "./useDashboardLanguage";
import { getCsrfToken } from "../lib/csrf";
import { getCookie, setCookie } from "../lib/cookies";
import { createRutorrentLanguageCarry } from "./rutorrentLanguage";
import { isLocaleCode, type LanguageContext } from "./locales";

const contextValue = (value: unknown): value is LanguageContext => {
  if (!value || typeof value !== "object") return false;
  const v = value as Partial<LanguageContext>;
  return typeof v.sessionContext === "string" && /^[a-f0-9]{64}$/.test(v.sessionContext) && isLocaleCode(v.locale) && ["session", "account", "browser", "dashboard", "fallback"].includes(v.source ?? "")
    && (v.accountLocale === null || isLocaleCode(v.accountLocale)) && (v.sessionLocale === null || isLocaleCode(v.sessionLocale))
    && (v.dashboardDefaultLocale === null || isLocaleCode(v.dashboardDefaultLocale))
    && Number.isSafeInteger(v.revision?.account) && Number.isSafeInteger(v.revision?.session)
    && (v.revision?.account ?? -1) >= 0 && (v.revision?.session ?? -1) >= 0
    && (v.revision?.account ?? Infinity) <= 2147483646 && (v.revision?.session ?? Infinity) <= 2147483646;
};
export { contextValue as isLanguageContext };

/**
 * Builds the provider's runtime. React may call this more than once for one mounted provider
 * (StrictMode replays state initializers), so it shares the one dashboard engine instead of
 * building its own.
 */
export function createLanguageRuntime() {
  const engine = dashboardEngine();
  const key = `qbx-language:${import.meta.env.BASE_URL}`;
  const rutorrent = createRutorrentLanguageCarry({ getCookie, setCookie });
  const controller = createLanguageController({
    languages: navigator.languages,
    readGuest: () => sessionStorage.getItem(key),
    writeGuest: locale => { if (locale) sessionStorage.setItem(key, locale); else sessionStorage.removeItem(key); },
    load: engine.load,
    save: async (choice, language) => {
      const csrf = getCsrfToken();
      const response = await fetch("/api/me/language", {
        method: "PATCH", headers: { "Content-Type": "application/json", ...(csrf ? { "x-csrf-token": csrf } : {}) },
        body: JSON.stringify({ scope: choice.scope, locale: choice.locale, revision: language.revision, sessionContext: language.sessionContext }),
      });
      const payload = await response.json().catch(() => null);
      return { status: response.status, code: payload?.code, language: contextValue(payload?.data?.language) ? payload.data.language : undefined };
    },
    hint: () => { try { const channel = new BroadcastChannel(key); channel.postMessage("refresh"); channel.close(); } catch { /* Heartbeat remains available. */ } },
    carryExternalLanguage: locale => rutorrent.carry(locale),
    resetExternalLanguage: () => rutorrent.reset(),
  });
  return { engine, controller, key };
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [runtime] = useState(createLanguageRuntime);
  useEffect(() => {
    let active = true;
    let publicRequest = 0;
    const reloadPublicConfig = async () => {
      const request = ++publicRequest;
      try {
        const response = await fetch("/api/system/config", { cache: "no-store" });
        if (!response.ok) throw new Error("Configuration unavailable");
        const payload = await response.json();
        if (active && request === publicRequest) {
          runtime.controller.setDashboardDefault(isLocaleCode(payload?.config?.dashboardDefaultLocale) ? payload.config.dashboardDefaultLocale : null);
        }
      } catch {
        if (active && request === publicRequest) runtime.controller.publicConfigFailed();
      }
    };
    runtime.controller.setPublicConfigRefresh(reloadPublicConfig);
    void reloadPublicConfig();
    let channel: BroadcastChannel | undefined;
    try { channel = new BroadcastChannel(runtime.key); channel.onmessage = () => { void runtime.controller.retryContext(); }; } catch { /* Focus and heartbeat refresh still work. */ }
    return () => { active = false; channel?.close(); };
  }, [runtime]);
  return <DashboardLanguageContext.Provider value={runtime.controller}><I18nextProvider i18n={runtime.engine.i18n}>{children}</I18nextProvider></DashboardLanguageContext.Provider>;
}
